import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AvaHiveOrb } from "@/components/ui/AvaHiveOrb";
import { OctonLogo } from "@/components/ui/OctonLogo";
import { ArrowLeft } from "lucide-react";
import { IMG } from "@/lib/assets";

export default function Login() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "client" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate("/app"); }, [user, navigate]);

  useEffect(() => {
    const sso = params.get("sso");
    if (params.get("error") === "google_not_configured") toast.error("Google sign-in isn't set up yet.");
    if (sso === "denied") toast.error("OCTON sign-in was declined or expired. Try again.");
    else if (sso === "missing") toast.error("No OCTON token returned. Please try signing in again.");
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      toast.success(mode === "login" ? "Welcome back" : "Welcome to OCTON Gym");
      let club = "";
      try { club = localStorage.getItem("octon_club_code") || ""; } catch (_) {}
      navigate(club ? `/app/membership?club=${club}` : "/app");
    } catch (err) {
      toast.error(apiErr(err, "Authentication failed"));
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    const origin = window.location.origin;
    window.location.href = `${BACKEND_URL}/api/auth/google/login?origin=${encodeURIComponent(origin)}`;
  };

  const avaLogin = () => {
    // Fleet single sign-on: OCTON mothership is the identity provider. Key stays server-side.
    const origin = window.location.origin;
    window.location.href = `${BACKEND_URL}/api/ava-sso/start?origin=${encodeURIComponent(origin)}`;
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 surface border-r border-[#1E222A] relative overflow-hidden">
        <img src={IMG.strength} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(0deg,#050505 12%,rgba(5,5,5,0.35) 60%,rgba(5,5,5,0.7))" }} />
        <button onClick={() => navigate("/")} className="relative flex items-center gap-2 text-sm text-[#8A91A6] hover:text-white transition-colors w-fit">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="relative flex justify-center"><AvaHiveOrb status="idle" size={230} predictionError={0.28} /></div>
        <div className="relative">
          <h2 className="font-heading font-black text-3xl tracking-tight mb-2">Welcome to OCTON Gym.</h2>
          <p className="text-[#8A91A6] max-w-sm">Your OCTON Gym coach learns how you train and adapts every session.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8 md:hidden">
            <OctonLogo size={30} />
            <span className="font-heading font-black">OCTON Gym</span>
          </div>
          <h1 className="font-heading font-black text-3xl tracking-tight mb-1">
            {mode === "login" ? "Sign in" : "Create account"}
          </h1>
          <p className="text-[#8A91A6] mb-8 text-sm">
            {mode === "login" ? "Good to see you again." : "Start your free 7-day taster. No card needed."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="mono-label">Name</Label>
                <Input data-testid="name-input" value={form.name} onChange={set("name")} required
                  className="mt-1.5 bg-[#0F1115] border-[#1E222A] h-11" placeholder="Your name" />
              </div>
            )}
            <div>
              <Label className="mono-label">Email</Label>
              <Input data-testid="email-input" type="email" value={form.email} onChange={set("email")} required
                className="mt-1.5 bg-[#0F1115] border-[#1E222A] h-11" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="mono-label">Password</Label>
              <Input data-testid="password-input" type="password" value={form.password} onChange={set("password")} required
                className="mt-1.5 bg-[#0F1115] border-[#1E222A] h-11" placeholder="••••••••" />
            </div>
            {mode === "register" && process.env.REACT_APP_ALLOW_TRAINER_SIGNUP && (
              <div>
                <Label className="mono-label">I am a</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {["client", "trainer"].map((r) => (
                    <button type="button" key={r} onClick={() => setForm({ ...form, role: r })}
                      data-testid={`role-${r}`}
                      className={`h-11 rounded-md text-sm capitalize border transition-colors ${
                        form.role === r ? "border-acid text-acid bg-acid/10" : "border-[#1E222A] text-[#8A91A6] hover:border-[#343A4A]"
                      }`}>{r}</button>
                  ))}
                </div>
              </div>
            )}
            {mode === "register" && (
              <p className="text-xs text-[#8A91A6]" data-testid="register-terms">
                By creating an account you agree to our <a href="/terms" target="_blank" rel="noreferrer" className="text-acid underline">Terms</a> and
                {" "}<a href="/privacy" target="_blank" rel="noreferrer" className="text-acid underline">Privacy Policy</a>. You must be 18 or over.
              </p>
            )}
            <Button type="submit" disabled={busy} data-testid="submit-btn"
              className="w-full h-11 bg-acid text-black hover:bg-[#AACC00] font-medium rounded-md">
              {busy ? "..." : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#1E222A]" /><span className="mono-label">or</span><div className="flex-1 h-px bg-[#1E222A]" />
          </div>
          <Button onClick={googleLogin} variant="outline" data-testid="google-login-btn"
            className="w-full h-11 border-[#343A4A] bg-transparent hover:bg-white/5 rounded-md">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </Button>

          <p className="text-center text-sm text-[#8A91A6] mt-6">
            {mode === "login" ? "New to OCTON?" : "Already have an account?"}{" "}
            <button data-testid="toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-acid hover:underline">{mode === "login" ? "Create account" : "Sign in"}</button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
